import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faDollarSign,
  faSpinner,
  faExclamationTriangle,
  faChartLine,
  faRobot,
  faCoins,
  faHashtag,
  faTimes,
  faArrowRight,
  faArrowLeft,
  faClock,
  faChevronRight,
  faSync
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../context/AuthContext';
import { getAgentName } from '../../utils/constants';
import MainLayout from '../Layout/MainLayout';

/**
 * Generic Spend Analysis Component
 * Works for any agent - pass agentId prop or use route param
 */
const SpendAnalysis = ({ agentId: propAgentId, agentName: propAgentName }) => {
  // Support both prop and route param
  const { agentId: routeAgentId } = useParams();
  const agentId = propAgentId || (routeAgentId ? parseInt(routeAgentId, 10) : null);
  const agentName = propAgentName || getAgentName(agentId);
  const { token } = useAuth();
  const [analysis, setAnalysis] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedRun, setSelectedRun] = useState(null);
  const [apiCallsDetail, setApiCallsDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [extraColumnName, setExtraColumnName] = useState(null);

  useEffect(() => {
    if (agentId) {
      fetchAnalysis();
    }
  }, [agentId]);

  const fetchAnalysis = async () => {
    try {
      setLoading(true);
      setError(null);

      const url = `/api/spend-analysis?agent_id=${agentId}`;

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch spend analysis');
      }

      const data = await response.json();
      setAnalysis(data.analysis || []);
      setSummary(data.summary || {});
      setExtraColumnName(data.extra_column_name || null);
    } catch (err) {
      console.error('Error fetching spend analysis:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchApiCallDetails = async (runId) => {
    try {
      setLoadingDetail(true);
      const response = await fetch(`/api/spend-analysis/detail/${runId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch API call details');
      }

      const data = await response.json();
      setApiCallsDetail(data);
    } catch (err) {
      console.error('Error fetching API call details:', err);
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleRowClick = (item) => {
    setSelectedRun(item);
    fetchApiCallDetails(item.run_id);
  };

  const closeModal = () => {
    setSelectedRun(null);
    setApiCallsDetail(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 4
    }).format(amount);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US').format(num);
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      'completed': 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      'running': 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
      'failed': 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
      'awaiting_user_input': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
    };
    return statusColors[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <MainLayout title="Spend Analysis">
        <div className="flex items-center justify-center min-h-96">
          <div className="text-center">
            <FontAwesomeIcon icon={faSpinner} className="text-4xl text-blue-500 animate-spin mb-4" />
            <p className="text-gray-600 dark:text-gray-400">Loading spend analysis...</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  if (error) {
    return (
      <MainLayout title="Spend Analysis">
        <div className="flex items-center justify-center min-h-96">
          <div className="text-center">
            <FontAwesomeIcon icon={faExclamationTriangle} className="text-4xl text-red-500 mb-4" />
            <p className="text-red-600 dark:text-red-400 mb-4">Error: {error}</p>
            <button onClick={fetchAnalysis} className="btn btn-primary">
              Try Again
            </button>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout title="Spend Analysis">
      <div className="space-y-6 p-6">
        {/* Summary Cards */}
        {summary && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="grid grid-cols-1 md:grid-cols-4 gap-4"
          >
            <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg shadow-lg p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-100 text-sm font-medium">Total Runs</p>
                  <p className="text-3xl font-bold mt-2">{formatNumber(summary.total_runs)}</p>
                </div>
                <FontAwesomeIcon icon={faChartLine} className="text-4xl opacity-50" />
              </div>
            </div>

            <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg shadow-lg p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-purple-100 text-sm font-medium">API Calls</p>
                  <p className="text-3xl font-bold mt-2">{formatNumber(summary.total_api_calls)}</p>
                </div>
                <FontAwesomeIcon icon={faRobot} className="text-4xl opacity-50" />
              </div>
            </div>

            <div className="bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-lg shadow-lg p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-yellow-100 text-sm font-medium">Total Tokens</p>
                  <p className="text-3xl font-bold mt-2">{formatNumber(summary.total_tokens)}</p>
                </div>
                <FontAwesomeIcon icon={faCoins} className="text-4xl opacity-50" />
              </div>
            </div>

            <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg shadow-lg p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-green-100 text-sm font-medium">Total Cost</p>
                  <p className="text-3xl font-bold mt-2">${summary.total_cost?.toFixed(2) || '0.00'}</p>
                </div>
                <FontAwesomeIcon icon={faDollarSign} className="text-4xl opacity-50" />
              </div>
            </div>
          </motion.div>
        )}

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white dark:bg-gray-800 rounded-lg shadow p-6"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center text-white">
                <FontAwesomeIcon icon={faDollarSign} className="text-xl" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                  {agentName ? `${agentName} Spend Analysis` : 'Spend Analysis'}
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  API costs and token usage for workflow runs
                </p>
              </div>
            </div>
            <button onClick={fetchAnalysis} className="btn btn-secondary flex items-center gap-2">
              <FontAwesomeIcon icon={faSync} />
              Refresh
            </button>
          </div>
        </motion.div>

        {/* Spend Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden"
        >
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Workflow Run
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    <FontAwesomeIcon icon={faRobot} className="mr-2" />
                    API Calls
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    <FontAwesomeIcon icon={faCoins} className="mr-2" />
                    Tokens
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    <FontAwesomeIcon icon={faDollarSign} className="mr-2" />
                    Cost
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Models Used
                  </th>
                  {extraColumnName && (
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      <FontAwesomeIcon icon={faHashtag} className="mr-2" />
                      {extraColumnName}
                    </th>
                  )}
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Details
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {analysis.length === 0 ? (
                  <tr>
                    <td colSpan={extraColumnName ? 7 : 6} className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">
                      <FontAwesomeIcon icon={faChartLine} className="text-4xl mb-3 opacity-50" />
                      <p className="text-lg font-medium">No data found</p>
                      <p className="text-sm">Run some workflows to see spend analysis</p>
                    </td>
                  </tr>
                ) : (
                  analysis.map((item, index) => (
                    <motion.tr
                      key={item.run_id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.03 }}
                      onClick={() => handleRowClick(item)}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm">
                          <div className="font-medium text-gray-900 dark:text-white">
                            {item.run_id.substring(0, 8)}...
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {item.workflow_name}
                          </div>
                          <div className="flex items-center gap-2 mt-1">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(item.status)}`}>
                              {item.status}
                            </span>
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            {formatDate(item.started_at)}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white font-semibold">
                        {formatNumber(item.api_calls)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white font-semibold">
                        {formatNumber(item.total_tokens)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm font-bold text-green-600 dark:text-green-400">
                          {formatCurrency(item.total_cost)}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-xs text-gray-700 dark:text-gray-300">
                          {item.models_used && item.models_used !== 'N/A' ? (
                            item.models_used.split(', ').map((model, i) => (
                              <span key={i} className="inline-block bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-2 py-1 rounded mr-1 mb-1">
                                {model}
                              </span>
                            ))
                          ) : (
                            <span className="text-gray-400">N/A</span>
                          )}
                        </div>
                      </td>
                      {extraColumnName && (
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {item.extra_column && item.extra_column !== 'N/A' ? (
                            <span className="text-blue-600 dark:text-blue-400">{item.extra_column}</span>
                          ) : (
                            <span className="text-gray-500 dark:text-gray-400">N/A</span>
                          )}
                        </td>
                      )}
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400 hover:bg-blue-200 dark:hover:bg-blue-800 transition-colors">
                          <FontAwesomeIcon icon={faChevronRight} />
                        </span>
                      </td>
                    </motion.tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </motion.div>

        {/* API Calls Detail Modal */}
        <AnimatePresence>
          {selectedRun && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
              onClick={closeModal}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.9, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: 20 }}
                className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[80vh] overflow-hidden"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Modal Header */}
                <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-4 flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-bold text-white">API Calls Detail</h3>
                    <p className="text-blue-100 text-sm">
                      Run ID: {selectedRun.run_id.substring(0, 12)}... | {selectedRun.workflow_name}
                    </p>
                  </div>
                  <button
                    onClick={closeModal}
                    className="text-white hover:text-gray-200 transition-colors"
                  >
                    <FontAwesomeIcon icon={faTimes} className="text-xl" />
                  </button>
                </div>

                {/* Modal Content */}
                <div className="p-6 overflow-y-auto max-h-[calc(80vh-120px)]">
                  {loadingDetail ? (
                    <div className="flex items-center justify-center py-12">
                      <FontAwesomeIcon icon={faSpinner} className="text-3xl text-blue-500 animate-spin" />
                      <span className="ml-3 text-gray-600 dark:text-gray-400">Loading API calls...</span>
                    </div>
                  ) : apiCallsDetail ? (
                    <>
                      {/* Summary Cards */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                        <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-4">
                          <p className="text-xs text-blue-600 dark:text-blue-400 font-medium">Total Calls</p>
                          <p className="text-2xl font-bold text-blue-700 dark:text-blue-300">{apiCallsDetail.summary?.total_calls || 0}</p>
                        </div>
                        <div className="bg-purple-50 dark:bg-purple-900/30 rounded-lg p-4">
                          <p className="text-xs text-purple-600 dark:text-purple-400 font-medium">Input Tokens</p>
                          <p className="text-2xl font-bold text-purple-700 dark:text-purple-300">{formatNumber(apiCallsDetail.summary?.total_prompt_tokens || 0)}</p>
                        </div>
                        <div className="bg-yellow-50 dark:bg-yellow-900/30 rounded-lg p-4">
                          <p className="text-xs text-yellow-600 dark:text-yellow-400 font-medium">Output Tokens</p>
                          <p className="text-2xl font-bold text-yellow-700 dark:text-yellow-300">{formatNumber(apiCallsDetail.summary?.total_completion_tokens || 0)}</p>
                        </div>
                        <div className="bg-green-50 dark:bg-green-900/30 rounded-lg p-4">
                          <p className="text-xs text-green-600 dark:text-green-400 font-medium">Total Cost</p>
                          <p className="text-2xl font-bold text-green-700 dark:text-green-300">{formatCurrency(apiCallsDetail.summary?.total_cost || 0)}</p>
                        </div>
                      </div>

                      {/* API Calls Table */}
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                          <thead className="bg-gray-50 dark:bg-gray-700">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">#</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Type</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Model</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                                <FontAwesomeIcon icon={faArrowRight} className="mr-1" />Input
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                                <FontAwesomeIcon icon={faArrowLeft} className="mr-1" />Output
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                                <FontAwesomeIcon icon={faDollarSign} className="mr-1" />Cost
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                                <FontAwesomeIcon icon={faClock} className="mr-1" />Time
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                            {apiCallsDetail.api_calls?.map((call, idx) => (
                              <tr key={call.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{idx + 1}</td>
                                <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{call.call_type}</td>
                                <td className="px-4 py-3">
                                  <span className="inline-block bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-2 py-1 rounded text-xs">
                                    {call.model}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-900 dark:text-white font-mono">
                                  {formatNumber(call.prompt_tokens)}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-900 dark:text-white font-mono">
                                  {formatNumber(call.completion_tokens)}
                                </td>
                                <td className="px-4 py-3 text-sm font-bold text-green-600 dark:text-green-400">
                                  ${call.cost?.toFixed(6) || '0.000000'}
                                </td>
                                <td className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400">
                                  {call.timestamp ? new Date(call.timestamp).toLocaleTimeString() : 'N/A'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {apiCallsDetail.api_calls?.length === 0 && (
                        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                          No API calls recorded for this workflow run.
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                      Failed to load API call details.
                    </div>
                  )}
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </MainLayout>
  );
};

export default SpendAnalysis;
